import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import {
  acceptedText,
  applyAccepted,
  isSourced,
  proposalCounts,
  type ContentProposal,
  type ProposedField,
} from "@shared/content-proposal";
import { localeMeta } from "@shared/i18n";
import { foyerApi } from "../../http/foyer";
import EditorCard from "../template/EditorCard";
import { EDITOR_MAX_WIDTH } from "../template/editorLayout";
import { tokens } from "../../theme";

/**
 * The review screen. Everything the model wrote passes through a person here.
 *
 * ## The two things this UI is responsible for
 *
 * 1. **Making an unsourced claim look different from a translation.** Every row
 *    shows the source text it came from, right there, so "is this right?" is
 *    answerable without opening another tab. A row with no source is tinted and
 *    labelled, and "Accept all" does not touch it - the button that gets pressed
 *    without reading can only ever accept text with a traceable origin.
 * 2. **Never implying anything is live.** Accepting folds text into the
 *    workspace draft the operator is already editing; the Save bar at the top of
 *    the app is what writes it, exactly as for a hand edit. The button says
 *    "Add to draft", not "Publish", because that is what it does.
 */

interface Props {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
  /** True while the workspace has unsaved edits - see `whyBlocked`. */
  dirty: boolean;
}

const POLL_MS = 2_000;

export default function TranslationReview({ premise, onChange, dirty }: Props) {
  const [availability, setAvailability] = useState<
    | { available: true; kind: string }
    | { available: false; reason: string }
    | null
  >(null);
  const [proposal, setProposal] = useState<ContentProposal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<{
    count: number;
    skipped: string[];
  } | null>(null);
  const pollRef = useRef<number | null>(null);

  useEffect(() => {
    foyerApi
      .aiAvailability()
      .then(setAvailability)
      .catch(() => {
        setAvailability({
          available: false,
          reason: "Could not reach the server to ask whether drafting is on.",
        });
      });
  }, []);

  // Load the most recent reviewable proposal for this premise, if any.
  useEffect(() => {
    if (!premise.premiseId) return;
    foyerApi
      .listProposals(premise.premiseId)
      .then(({ proposals }) => {
        const open = proposals.find(
          (p) => p.state === "ready" || p.state === "generating"
        );
        setProposal(open ?? null);
      })
      .catch(() => setProposal(null));
  }, [premise.premiseId]);

  // Poll while a run is in flight. The proposal row IS the progress indicator -
  // the request that started it returned immediately, so a slow model never
  // becomes a hanging fetch a proxy will cut at sixty seconds.
  useEffect(() => {
    if (proposal?.state !== "generating") {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    const id = proposal.id;
    pollRef.current = window.setInterval(() => {
      foyerApi
        .getProposal(id)
        .then(setProposal)
        .catch(() => {
          /* transient; the next tick tries again */
        });
    }, POLL_MS);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [proposal?.state, proposal?.id]);

  const counts = useMemo(
    () => proposalCounts(proposal?.fields ?? []),
    [proposal]
  );

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    setApplied(null);
    try {
      setProposal(await foyerApi.startTranslation(premise.premiseId));
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }, [premise.premiseId]);

  const decide = useCallback(
    async (
      field: ProposedField,
      decision: ProposedField["decision"],
      edited?: string
    ) => {
      if (!proposal) return;
      try {
        setProposal(
          await foyerApi.decideProposalField({
            id: proposal.id,
            fieldId: field.id,
            decision,
            edited,
          })
        );
      } catch (err) {
        setError(messageOf(err));
      }
    },
    [proposal]
  );

  /**
   * Fold the accepted fields into the workspace draft.
   *
   * `applyAccepted` re-checks each target against the CONTENT AS IT IS NOW, so
   * anything the operator edited during the review is reported back rather than
   * overwritten. Those refusals are shown - a silent skip would look like the
   * button half-worked.
   */
  const addToDraft = useCallback(async () => {
    if (!proposal || !premise.scheme) return;
    setBusy(true);
    setError(null);
    try {
      const result = applyAccepted(
        premise.scheme.content,
        proposal.fields,
        proposal.defaultLocale
      );
      onChange({
        ...premise,
        scheme: { ...premise.scheme, content: result.content },
      });
      setProposal(
        await foyerApi.markProposalApplied(proposal.id, result.applied)
      );
      setApplied({
        count: result.applied.length,
        skipped: result.skipped.map(
          (s) =>
            `${s.field.label} (${localeMeta(s.field.locale).label}) — ${
              s.reason === "changed_since_generated"
                ? `you changed this to “${s.actual}” while reviewing, so it was left alone`
                : "that field no longer exists in this template"
            }`
        ),
      });
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }, [proposal, premise, onChange]);

  if (availability && !availability.available) {
    return (
      <Box sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
        <Alert severity="info">
          <strong>Drafting is not switched on for this deployment.</strong>{" "}
          {availability.reason}
        </Alert>
      </Box>
    );
  }

  const blocked = whyBlocked(premise, dirty);

  return (
    <Stack spacing={2} sx={{ maxWidth: EDITOR_MAX_WIDTH }}>
      <EditorCard
        eyebrow="Translate"
        action={
          <Button
            variant="contained"
            size="small"
            disabled={busy || !!blocked || proposal?.state === "generating"}
            onClick={start}
          >
            {proposal?.state === "generating"
              ? "Working…"
              : "Draft translations"}
          </Button>
        }
      >
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          Fills every language this premise supports from the text already
          written in{" "}
          <strong>{localeMeta(premise.defaultLocale || "en").label}</strong>.
          Nothing is published: you review every line, and what you accept lands
          in this editor as unsaved changes.
        </Typography>
        {blocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {blocked}
          </Alert>
        )}
        {availability?.available && availability.kind === "fake" && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This environment has no API key, so translations come back as{" "}
            <code>[sv] the original text</code>. Useful for trying the review
            flow; not useful as content.
          </Alert>
        )}
      </EditorCard>

      {error && <Alert severity="error">{error}</Alert>}

      {applied && (
        <Alert severity={applied.skipped.length ? "warning" : "success"}>
          <strong>
            {applied.count} translation{applied.count === 1 ? "" : "s"} added to
            this editor.
          </strong>{" "}
          They are not saved yet — use Save at the top of the page.
          {applied.skipped.length > 0 && (
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
              {applied.skipped.map((s) => (
                <li key={s}>
                  <Typography variant="caption">{s}</Typography>
                </li>
              ))}
            </Box>
          )}
        </Alert>
      )}

      {proposal?.state === "generating" && (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            Drafting translations for{" "}
            {proposal.locales.map((l) => localeMeta(l).label).join(", ")}. You
            can leave this page; the draft will be here when you come back.
          </Typography>
        </Stack>
      )}

      {proposal?.state === "failed" && (
        <Alert severity="error">
          <strong>The translation run did not finish.</strong> {proposal.error}{" "}
          Nothing was changed.
        </Alert>
      )}

      {proposal?.state === "ready" && (
        <ReviewList
          proposal={proposal}
          counts={counts}
          busy={busy}
          onDecide={decide}
          onAcceptSourced={async () => {
            try {
              setProposal(await foyerApi.acceptSourcedFields(proposal.id));
            } catch (err) {
              setError(messageOf(err));
            }
          }}
          onAddToDraft={addToDraft}
          onDiscard={async () => {
            try {
              await foyerApi.discardProposal(proposal.id);
              setProposal(null);
            } catch (err) {
              setError(messageOf(err));
            }
          }}
        />
      )}
    </Stack>
  );
}

/** Why the button is disabled, in words the operator can act on. */
function whyBlocked(premise: PremiseConfig, dirty: boolean): string | null {
  if (!premise.scheme) {
    return "Pick a template first — there is no content to translate yet.";
  }
  if (
    (premise.supportedLocales ?? []).filter((l) => l !== premise.defaultLocale)
      .length === 0
  ) {
    return "This premise supports only one language. Add another under Settings → Languages.";
  }
  if (dirty) {
    // Deliberate: the run reads the SAVED config. Translating unsaved text would
    // produce rows that `applyAccepted` then refuses as `field_gone`, which is a
    // baffling way to find out you had unsaved changes.
    return "Save your changes first — translations are drafted from the saved version of this premise.";
  }
  return null;
}

function ReviewList({
  proposal,
  counts,
  busy,
  onDecide,
  onAcceptSourced,
  onAddToDraft,
  onDiscard,
}: {
  proposal: ContentProposal;
  counts: ReturnType<typeof proposalCounts>;
  busy: boolean;
  onDecide: (
    f: ProposedField,
    d: ProposedField["decision"],
    edited?: string
  ) => void;
  onAcceptSourced: () => void;
  onAddToDraft: () => void;
  onDiscard: () => void;
}) {
  const byLocale = useMemo(() => {
    const map = new Map<string, ProposedField[]>();
    for (const f of proposal.fields) {
      const list = map.get(f.locale) ?? [];
      list.push(f);
      map.set(f.locale, list);
    }
    return [...map.entries()];
  }, [proposal.fields]);

  return (
    <>
      <EditorCard
        eyebrow="Review"
        action={
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={onDiscard} disabled={busy}>
              Discard
            </Button>
            <Tooltip
              title={
                counts.unsourced > 0
                  ? `${counts.unsourced} line(s) have no source text and must be accepted individually.`
                  : ""
              }
            >
              <span>
                <Button
                  size="small"
                  onClick={onAcceptSourced}
                  disabled={busy || counts.pending === counts.unsourced}
                >
                  Accept all
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              onClick={onAddToDraft}
              disabled={busy || counts.accepted === 0}
            >
              Add {counts.accepted} to draft
            </Button>
          </Stack>
        }
      >
        {/* A review list nobody asked for has to explain itself. Without this
            line, an operator who added a language yesterday opens Languages and
            finds forty rows of German waiting with no account of where they
            came from - which reads as the system having done something on its
            own, not as a convenience. */}
        {autoReason(proposal) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {autoReason(proposal)} Nothing has been changed — review the lines
            below and add the ones you want.
          </Alert>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={`${counts.total} drafted`} />
          <Chip
            size="small"
            color="success"
            label={`${counts.accepted} accepted`}
          />
          <Chip size="small" label={`${counts.pending} to review`} />
          {counts.rejected > 0 && (
            <Chip size="small" label={`${counts.rejected} rejected`} />
          )}
          {counts.edited > 0 && (
            <Chip
              size="small"
              color="info"
              label={`${counts.edited} reworded`}
            />
          )}
        </Stack>

        {proposal.notes.length > 0 && (
          <Box component="ul" sx={{ mt: 2, mb: 0, pl: 2.5 }}>
            {proposal.notes.map((n) => (
              <li key={n.code}>
                <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
                  {n.detail}
                </Typography>
              </li>
            ))}
          </Box>
        )}

        {proposal.generator && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 2, color: tokens.inkFaint }}
          >
            Drafted by {proposal.generator.provider} ·{" "}
            {proposal.generator.model} · prompt v
            {proposal.generator.promptVersion}
          </Typography>
        )}
      </EditorCard>

      {byLocale.map(([locale, fields]) => (
        <EditorCard
          key={locale}
          eyebrow={localeMeta(locale).label}
          action={
            <Typography variant="caption" sx={{ color: tokens.inkMuted }}>
              {fields.filter((f) => f.decision === "accepted").length} /{" "}
              {fields.length} accepted
            </Typography>
          }
        >
          <Stack divider={<Divider flexItem />} spacing={0}>
            {fields.map((field) => (
              <FieldRow key={field.id} field={field} onDecide={onDecide} />
            ))}
          </Stack>
        </EditorCard>
      ))}
    </>
  );
}

function FieldRow({
  field,
  onDecide,
}: {
  field: ProposedField;
  onDecide: (
    f: ProposedField,
    d: ProposedField["decision"],
    edited?: string
  ) => void;
}) {
  const [draft, setDraft] = useState(acceptedText(field));
  const sourced = isSourced(field);

  // Re-seed the box when the row is replaced by a server response. `field` is a
  // fresh object on every fetch, so depending on the whole thing is both what
  // the linter wants and what is correct: a decision round-trip must put the
  // canonical text back, or a reviewer's edit and the stored value drift apart.
  useEffect(() => setDraft(acceptedText(field)), [field]);

  return (
    <Box
      sx={{
        py: 1.75,
        // An unsourced row is tinted for the whole width. It is the one visual
        // difference that has to survive a tired operator scrolling quickly.
        ...(sourced ? {} : { bgcolor: tokens.rowAlt, px: 1.5, mx: -1.5 }),
        opacity: field.decision === "rejected" ? 0.5 : 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: 0.75 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            letterSpacing: ".09em",
            fontWeight: 700,
            color: tokens.inkFaint,
          }}
        >
          {field.label}
        </Typography>
        {!sourced && (
          <Chip
            size="small"
            color="warning"
            label="No source — check this yourself"
          />
        )}
        {field.decision === "accepted" && (
          <Chip size="small" color="success" label="Accepted" />
        )}
      </Stack>

      {field.evidence && (
        <Typography
          variant="body2"
          sx={{ color: tokens.inkMuted, mb: 1, fontStyle: "italic" }}
        >
          “{field.evidence.quote}”
        </Typography>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems="flex-start"
      >
        <TextField
          fullWidth
          size="small"
          multiline
          maxRows={6}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft !== acceptedText(field)) {
              onDecide(field, "accepted", draft);
            }
          }}
        />
        {/* Explicit basis on both sides: a flex row with one unbounded prose
            column collapses its sibling - the trap the card-language note
            records from the header and the device dialog. */}
        <Stack direction="row" spacing={1} sx={{ flex: "0 0 auto" }}>
          <Button
            size="small"
            variant={field.decision === "accepted" ? "contained" : "outlined"}
            onClick={() => onDecide(field, "accepted", draft)}
          >
            Accept
          </Button>
          <Button
            size="small"
            color="inherit"
            variant={field.decision === "rejected" ? "contained" : "outlined"}
            onClick={() => onDecide(field, "rejected")}
          >
            Reject
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

/**
 * The sentence a self-started run left on its first history entry, if it was
 * self-started. A run somebody pressed the button for carries none, and says
 * nothing extra.
 */
function autoReason(proposal: ContentProposal): string | null {
  return proposal.history?.[0]?.note ?? null;
}

function messageOf(err: unknown): string {
  if (err && typeof err === "object" && "message" in err) {
    return String((err as { message: unknown }).message);
  }
  return "Something went wrong.";
}
